import { useEffect, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { functionError } from '@/hooks/useMix'
import '@/styles/gg.css'
import '@/styles/newsletter.css'

/**
 * The newsletter's public pages (docs/RESEND-HANDOFF.md 3.3, Brian's Your list:
 * "Your signup page. Share it anywhere; new subscribers confirm by email
 * before their first issue").
 *
 *   /n/<handle>   the signup page: email, then a confirm email
 *   /c/<token>    the confirm link in that email
 *   /u/<token>    the unsubscribe link at the foot of every issue
 *
 * No sign-in. Everything goes through the newsletter-public function, which
 * checks the signed tokens.
 */
async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('newsletter-public', { body })
  if (error) throw await functionError(error)
  return data as T
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="gg nlp">
      <div className="nlp__card">{children}</div>
      <p className="nlp__foot">Sent with GoGrov</p>
    </div>
  )
}

export function NewsletterSignup() {
  const { handle = '' } = useParams<{ handle: string }>()
  const [info, setInfo] = useState<{ name: string; place: string } | null>(null)
  const [missing, setMissing] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [trap, setTrap] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    call<{ name: string; place: string }>({ a: 'info', handle }).then(setInfo).catch(() => setMissing(true))
  }, [handle])
  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { setErr('That doesn’t look like an email address.'); return }
    setErr(null); setState('busy')
    try { await call({ a: 'signup', handle, email: email.trim(), name: name.trim(), website: trap }); setState('done') } catch (x) { setErr((x as Error).message); setState('idle') }
  }
  if (missing) return <Shell><h1 className="nlp__h">There’s no newsletter at this address.</h1><p className="nlp__p">Check the link with whoever shared it.</p></Shell>
  if (!info) return <Shell><span className="gg-spinner" /></Shell>
  const town = info.place.split(',')[0].trim()
  return (
    <Shell>
      <span className="nlp__eb">Newsletter</span>
      <h1 className="nlp__h">{info.name}</h1>
      <p className="nlp__p">What’s happening around {town || 'town'}, every week, in your inbox.</p>
      {state === 'done' ? (
        <div className="nlp__ok"><b>Check your inbox.</b> We sent a link to {email.trim()}. Click it to confirm and your first issue comes next week.</div>
      ) : (
        <form className="nlp__form" onSubmit={(e) => void submit(e)}>
          <label className="nlp__lbl" htmlFor="nlp-email">Email</label>
          <input id="nlp-email" className="nls-in" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setErr(null) }} />
          <label className="nlp__lbl" htmlFor="nlp-name">Name <em>optional</em></label>
          <input id="nlp-name" className="nls-in" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="nlp__trap" tabIndex={-1} autoComplete="off" aria-hidden="true" value={trap} onChange={(e) => setTrap(e.target.value)} name="website" />
          {err && <p className="gg-error">{err}</p>}
          <button type="submit" className="gg-btn gg-btn--amber nlp__go" disabled={state === 'busy'}>{state === 'busy' ? 'Signing you up' : 'Sign me up'}</button>
          <p className="nlp__fine">We’ll email you a link to confirm. Unsubscribe any time from the foot of any issue.</p>
        </form>
      )}
    </Shell>
  )
}

function TokenPage({ action }: { action: 'confirm' | 'unsub' }) {
  const { token = '' } = useParams<{ token: string }>()
  const [res, setRes] = useState<{ ok?: boolean; name?: string; test?: boolean; error?: string } | null>(null)
  useEffect(() => {
    call<{ ok: boolean; name?: string; test?: boolean }>({ a: action, t: token }).then(setRes).catch((e) => setRes({ error: (e as Error).message }))
  }, [action, token])
  if (!res) return <Shell><span className="gg-spinner" /></Shell>
  if (res.error) return <Shell><h1 className="nlp__h">That didn’t work.</h1><p className="nlp__p">{res.error}</p></Shell>
  if (action === 'confirm') {
    return <Shell><span className="nlp__eb">Newsletter</span><h1 className="nlp__h">You’re on the list.</h1><p className="nlp__p">The next issue of {res.name || 'the newsletter'} comes to your inbox. Unsubscribe any time from the foot of any issue.</p></Shell>
  }
  if (res.test) return <Shell><h1 className="nlp__h">This was a test.</h1><p className="nlp__p">In a real issue this link takes the reader off the list in one click.</p></Shell>
  return <Shell><span className="nlp__eb">Newsletter</span><h1 className="nlp__h">You’re unsubscribed.</h1><p className="nlp__p">You won’t get {res.name || 'this newsletter'} any more. Changed your mind? Sign up again from its page.</p></Shell>
}

export function NewsletterConfirm() { return <TokenPage action="confirm" /> }
export function NewsletterUnsubscribe() { return <TokenPage action="unsub" /> }
